/**
 * ImportExportPage — Import / Export / History tabs.
 *
 * Import is the guided CSV wizard (papaparse, column mapping with
 * join/split transforms, server-normalized preview, chunked batch
 * upload). History lists past import batches with archive/restore —
 * the undo path for a bad import. Export streams CSV from the server
 * as before. The active tab syncs to ?tab= so the wizard's "View
 * import history" link and external deep-links work.
 */

import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Download } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { FiasLeadsImportEntityType } from '@/lib/api';
import { ImportWizard } from '../components/import/ImportWizard';
import { ImportHistorySection } from '../components/import/ImportHistorySection';

interface Props {
  holderId: string;
}

const TAB_VALUES = ['import', 'export', 'history'] as const;
type TabValue = (typeof TAB_VALUES)[number];

function ExportCard({ holderId }: Props) {
  const [entityType, setEntityType] = useState<FiasLeadsImportEntityType>('contact');
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      const csv = await api.fiasLeadsExport(holderId, entityType);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `fias-leads-${entityType}s.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="rounded-lg border border-border bg-card p-4 flex flex-col gap-3 max-w-md">
      <p className="text-sm text-muted-foreground">
        Download your data as a CSV file you can open in any spreadsheet.
      </p>
      <div className="flex items-center gap-2">
        <Select
          value={entityType}
          onValueChange={(v) => setEntityType(v as FiasLeadsImportEntityType)}
        >
          <SelectTrigger className="w-40" aria-label="Export type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="contact">Contacts</SelectItem>
            <SelectItem value="company">Companies</SelectItem>
            <SelectItem value="deal">Deals</SelectItem>
          </SelectContent>
        </Select>
        <Button type="button" onClick={handleExport} disabled={exporting}>
          <Download className="mr-2 h-4 w-4" />
          {exporting ? 'Exporting…' : `Export ${entityType}s`}
        </Button>
      </div>
    </div>
  );
}

export function ImportExportPage({ holderId }: Props) {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab');
  const tab: TabValue = TAB_VALUES.includes(rawTab as TabValue) ? (rawTab as TabValue) : 'import';

  const setTab = (next: string) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.set('tab', next);
        return params;
      },
      { replace: true },
    );
  };

  return (
    <div className="p-6 max-w-5xl mx-auto flex flex-col gap-4">
      <header>
        <h1 className="text-2xl font-semibold">Import / Export</h1>
        <p className="text-sm text-muted-foreground">
          Bring in contacts, companies, or deals from any CSV — or download what you have.
        </p>
      </header>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="import">Import</TabsTrigger>
          <TabsTrigger value="export">Export</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
        </TabsList>
        <TabsContent value="import" className="pt-4">
          <ImportWizard holderId={holderId} onViewHistory={() => setTab('history')} />
        </TabsContent>
        <TabsContent value="export" className="pt-4">
          <ExportCard holderId={holderId} />
        </TabsContent>
        <TabsContent value="history" className="pt-4">
          <ImportHistorySection holderId={holderId} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
